/**
 * Cavalry (骑兵) - Gray-blue armor, horse with rider, lance
 * Mounted warrior combination
 */

import * as THREE from 'three';
import { COLORS } from '../unitConstants';
import { mergeGeometries } from '../unitHelpers';

export function createCavalryGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const colors: THREE.Color[] = [];

  // ========== HORSE ==========

  // === Horse Body ===
  const horseBody = new THREE.SphereGeometry(0.18, 10, 8);
  horseBody.scale(1.5, 0.9, 0.85);
  horseBody.translate(0, 0.22, 0);
  parts.push(horseBody);
  colors.push(COLORS.horseBrown);

  // Horse chest
  const chest = new THREE.SphereGeometry(0.12, 8, 6);
  chest.scale(1.0, 1.1, 0.9);
  chest.translate(0.18, 0.24, 0);
  parts.push(chest);
  colors.push(COLORS.horseBrown);

  // Horse rump
  const rump = new THREE.SphereGeometry(0.13, 8, 6);
  rump.scale(1.0, 1.0, 0.9);
  rump.translate(-0.16, 0.24, 0);
  parts.push(rump);
  colors.push(COLORS.horseBrown);

  // === Horse Head ===
  const horseHead = new THREE.SphereGeometry(0.08, 8, 6);
  horseHead.scale(1.4, 1.0, 0.85);
  horseHead.translate(0.36, 0.32, 0);
  parts.push(horseHead);
  colors.push(COLORS.horseBrown);

  // Snout
  const snout = new THREE.CylinderGeometry(0.035, 0.045, 0.10, 6);
  snout.rotateZ(Math.PI / 2 + 0.3);
  snout.translate(0.46, 0.30, 0);
  parts.push(snout);
  colors.push(COLORS.horseDark);

  // Ears
  for (const z of [-0.04, 0.04]) {
    const ear = new THREE.ConeGeometry(0.02, 0.05, 4);
    ear.rotateX(z > 0 ? -0.3 : 0.3);
    ear.translate(0.34, 0.40, z);
    parts.push(ear);
    colors.push(COLORS.horseBrown);
  }

  // === Horse Mane ===
  const mane = new THREE.BoxGeometry(0.15, 0.05, 0.02);
  mane.translate(0.25, 0.38, 0);
  parts.push(mane);
  colors.push(COLORS.mane);

  // === Horse Legs (4) ===
  const legPositions = [
    { x: 0.16, z: 0.07, front: true },
    { x: 0.16, z: -0.07, front: true },
    { x: -0.14, z: 0.07, front: false },
    { x: -0.14, z: -0.07, front: false },
  ];

  for (const pos of legPositions) {
    const upperLeg = new THREE.CylinderGeometry(0.03, 0.025, 0.10, 5);
    upperLeg.translate(pos.x, 0.12, pos.z);
    parts.push(upperLeg);
    colors.push(COLORS.horseBrown);

    const lowerLeg = new THREE.CylinderGeometry(0.025, 0.02, 0.10, 5);
    lowerLeg.translate(pos.x, 0.03, pos.z);
    parts.push(lowerLeg);
    colors.push(COLORS.horseBrown);

    const hoof = new THREE.CylinderGeometry(0.025, 0.028, 0.025, 6);
    hoof.translate(pos.x, -0.01, pos.z);
    parts.push(hoof);
    colors.push(COLORS.hair);
  }

  // === Horse Tail ===
  const tailBase = new THREE.CylinderGeometry(0.025, 0.015, 0.12, 5);
  tailBase.rotateX(0.5);
  tailBase.translate(-0.30, 0.22, 0);
  parts.push(tailBase);
  colors.push(COLORS.mane);

  // === Saddle ===
  const saddle = new THREE.BoxGeometry(0.16, 0.04, 0.14);
  saddle.translate(0, 0.34, 0);
  parts.push(saddle);
  colors.push(COLORS.woodDark);

  // Saddle blanket (with faction color)
  const blanket = new THREE.BoxGeometry(0.20, 0.015, 0.18);
  blanket.translate(0, 0.31, 0);
  parts.push(blanket);
  colors.push(COLORS.cavalryGray);

  // Stirrups
  for (const z of [-0.09, 0.09]) {
    const stirrup = new THREE.TorusGeometry(0.025, 0.006, 4, 8);
    stirrup.translate(0, 0.16, z);
    parts.push(stirrup);
    colors.push(COLORS.bronze);
  }

  // ========== RIDER (smaller Q-style, 0.75 scale) ==========
  const riderScale = 0.75;
  const riderBaseY = 0.36;

  // === Rider Head ===
  const riderHead = new THREE.SphereGeometry(0.085 * riderScale, 8, 6);
  riderHead.translate(0, riderBaseY + 0.26, 0);
  parts.push(riderHead);
  colors.push(COLORS.skin);

  // Rider helmet (simplified Chinese style)
  const riderHelmet = new THREE.SphereGeometry(
    0.09 * riderScale,
    6,
    5,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.55
  );
  riderHelmet.translate(0, riderBaseY + 0.30, 0);
  parts.push(riderHelmet);
  colors.push(COLORS.cavalryGray);

  // Helmet spike
  const helmetSpike = new THREE.ConeGeometry(
    0.015 * riderScale,
    0.04 * riderScale,
    5
  );
  helmetSpike.translate(0, riderBaseY + 0.36, 0);
  parts.push(helmetSpike);
  colors.push(COLORS.gold);

  // === Rider Body ===
  const riderBody = new THREE.CylinderGeometry(
    0.055 * riderScale,
    0.065 * riderScale,
    0.10 * riderScale,
    6
  );
  riderBody.translate(0, riderBaseY + 0.14, 0);
  parts.push(riderBody);
  colors.push(COLORS.cavalryGray);

  // === Rider Cape (flowing behind) ===
  const cape = new THREE.BoxGeometry(0.08, 0.12, 0.01);
  cape.rotateX(0.4);
  cape.translate(-0.02, riderBaseY + 0.12, -0.05);
  parts.push(cape);
  colors.push(COLORS.cavalryGray);

  // === Rider Arms ===
  // Left arm (holding reins)
  const leftArm = new THREE.CylinderGeometry(0.016, 0.013, 0.07, 5);
  leftArm.rotateZ(0.7);
  leftArm.translate(-0.05, riderBaseY + 0.15, 0.03);
  parts.push(leftArm);
  colors.push(COLORS.cavalryGray);

  // Right arm (raised with lance)
  const rightArm = new THREE.CylinderGeometry(0.016, 0.013, 0.07, 5);
  rightArm.rotateZ(-0.8);
  rightArm.translate(0.05, riderBaseY + 0.17, 0);
  parts.push(rightArm);
  colors.push(COLORS.cavalryGray);

  const rightHand = new THREE.SphereGeometry(0.014, 4, 3);
  rightHand.translate(0.10, riderBaseY + 0.20, 0);
  parts.push(rightHand);
  colors.push(COLORS.skin);

  // === Rider Legs (bent for riding) ===
  for (const z of [-0.05, 0.05]) {
    const leg = new THREE.CylinderGeometry(0.020, 0.016, 0.08, 5);
    leg.rotateX(z > 0 ? 0.3 : -0.3);
    leg.translate(0, riderBaseY + 0.02, z * 1.2);
    parts.push(leg);
    colors.push(COLORS.cavalryGray);
  }

  // === WEAPON: Lance (马槊/骑枪) ===
  // Lance shaft
  const lanceShaft = new THREE.CylinderGeometry(0.012, 0.016, 0.70, 5);
  lanceShaft.rotateZ(-0.3);
  lanceShaft.translate(0.22, riderBaseY + 0.35, 0);
  parts.push(lanceShaft);
  colors.push(COLORS.wood);

  // Lance head
  const lanceHead = new THREE.ConeGeometry(0.025, 0.10, 4);
  lanceHead.rotateZ(-0.3);
  lanceHead.translate(0.32, riderBaseY + 0.65, 0);
  parts.push(lanceHead);
  colors.push(COLORS.steel);

  // Lance tassel
  const lanceTassel = new THREE.SphereGeometry(0.012, 5, 4);
  lanceTassel.translate(0.28, riderBaseY + 0.58, 0);
  parts.push(lanceTassel);
  colors.push(COLORS.tassel);

  return mergeGeometries(parts, colors);
}
